import React, { useEffect, useRef, useState } from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeTask(type, data) {
  return {
    type,
    data,
    tags: ["kolejnosc_dzialan", "liczby_naturalne"],
  };
}

function estimateMathWidth(math) {
  if (typeof document === "undefined") return math.length * 14;
  const canvas = estimateMathWidth.canvas || document.createElement("canvas");
  estimateMathWidth.canvas = canvas;
  const ctx = canvas.getContext("2d");
  if (!ctx) return math.length * 14;
  ctx.font = '700 24px KaTeX_Main, "Times New Roman", serif';
  const plain = String(math)
    .replace(/\\cdot/g, "·")
    .replace(/\\left|\\right/g, "")
    .replace(/[{}]/g, "");
  return ctx.measureText(plain).width + 12;
}

function buildChainLines(parts, maxWidth) {
  if (!maxWidth || maxWidth < 80) return [{ start: 0, end: parts.length - 1 }];

  const widths = parts.map(estimateMathWidth);
  const eqWidth = estimateMathWidth("=");
  const lines = [];
  let start = 0;

  function lineWidth(from, to, willContinue) {
    const leadingEquals = from > 0 ? eqWidth : 0;
    const internalEquals = Math.max(0, to - from) * eqWidth;
    const trailingEquals = willContinue ? eqWidth : 0;
    const expressionWidth = widths.slice(from, to + 1).reduce((sum, width) => sum + width, 0);
    return leadingEquals + internalEquals + trailingEquals + expressionWidth;
  }

  while (start < parts.length) {
    let end = start;
    for (let candidate = start; candidate < parts.length; candidate += 1) {
      const willContinue = candidate < parts.length - 1;
      if (lineWidth(start, candidate, willContinue) <= maxWidth || candidate === start) {
        end = candidate;
      } else {
        break;
      }
    }
    lines.push({ start, end });
    start = end + 1;
  }

  return lines;
}

function EquationChain({ parts }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const updateWidth = () => setWidth(node.clientWidth);
    updateWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const lines = buildChainLines(parts, Math.max(0, width - 24));

  return (
    <div ref={ref} style={{ width: "100%" }}>
      {lines.map(({ start, end }, index) => {
        const isFirst = start === 0;
        const hasNext = end < parts.length - 1;
        const math = `${isFirst ? "" : "="}${parts.slice(start, end + 1).join("=")}${hasNext ? "=" : ""}`;
        return <BlockMath key={`${start}-${end}-${index}`} math={math} />;
      })}
    </div>
  );
}

function addSubTask() {
  const a = randInt(20, 80);
  const b = randInt(6, 25);
  const c = randInt(4, Math.min(30, a + b - 2));
  return makeTask("OrderAddSubTask", {
    latex: `${a}+${b}-${c}`,
    chainParts: [`${a}+${b}-${c}`, `${a + b}-${c}`, `${a + b - c}`],
    result: a + b - c,
  });
}

function subAddTask() {
  const a = randInt(35, 90);
  const b = randInt(8, 30);
  const c = randInt(5, 35);
  return makeTask("OrderSubAddTask", {
    latex: `${a}-${b}+${c}`,
    chainParts: [`${a}-${b}+${c}`, `${a - b}+${c}`, `${a - b + c}`],
    result: a - b + c,
  });
}

function mulDivTask() {
  const divisor = randInt(2, 9);
  const quotient = randInt(2, 9);
  const factor = randInt(2, 9);
  const a = divisor * quotient;
  const product = a * factor;
  return makeTask("OrderMulDivTask", {
    latex: `${a}\\cdot ${factor}:${divisor}`,
    chainParts: [`${a}\\cdot ${factor}:${divisor}`, `${product}:${divisor}`, `${product / divisor}`],
    result: product / divisor,
  });
}

function addMulTask() {
  const a = randInt(8, 40);
  const b = randInt(2, 9);
  const c = randInt(2, 9);
  const product = b * c;
  return makeTask("OrderAddMulTask", {
    latex: `${a}+${b}\\cdot ${c}`,
    chainParts: [`${a}+${b}\\cdot ${c}`, `${a}+${product}`, `${a + product}`],
    result: a + product,
  });
}

function parenthesesTask() {
  const a = randInt(5, 25);
  const b = randInt(2, 12);
  const c = randInt(2, 8);
  const sum = a + b;
  return makeTask("OrderParenthesesTask", {
    latex: `(${a}+${b})\\cdot ${c}`,
    chainParts: [`(${a}+${b})\\cdot ${c}`, `${sum}\\cdot ${c}`, `${sum * c}`],
    result: sum * c,
  });
}

function mixedTask() {
  const variant = randInt(0, 3);
  const b = randInt(2, 8);
  const c = randInt(2, 8);
  const product = b * c;
  const divisor = pick([2, 3, 4, 5]);
  const quotient = randInt(2, 9);
  const dividend = divisor * quotient;

  if (variant === 1) {
    const a = product + randInt(10, 35);
    const result = a - product + quotient;
    return makeTask("OrderMixedTask", {
      latex: `${a}-${b}\\cdot ${c}+${dividend}:${divisor}`,
      chainParts: [
        `${a}-${b}\\cdot ${c}+${dividend}:${divisor}`,
        `${a}-${product}+${quotient}`,
        `${a - product}+${quotient}`,
        `${result}`,
      ],
      result,
    });
  }

  if (variant === 2) {
    const a = randInt(10, 35);
    const result = a + quotient + product;
    return makeTask("OrderMixedTask", {
      latex: `${a}+${dividend}:${divisor}+${b}\\cdot ${c}`,
      chainParts: [
        `${a}+${dividend}:${divisor}+${b}\\cdot ${c}`,
        `${a}+${quotient}+${product}`,
        `${a + quotient}+${product}`,
        `${result}`,
      ],
      result,
    });
  }

  if (variant === 3) {
    const a = randInt(10, 35);
    const result = a + product + quotient;
    return makeTask("OrderMixedTask", {
      latex: `${a}+${b}\\cdot ${c}+${dividend}:${divisor}`,
      chainParts: [
        `${a}+${b}\\cdot ${c}+${dividend}:${divisor}`,
        `${a}+${product}+${quotient}`,
        `${a + product}+${quotient}`,
        `${result}`,
      ],
      result,
    });
  }

  const a = randInt(10, 35);
  const result = a + product - quotient;
  return makeTask("OrderMixedTask", {
    latex: `${a}+${b}\\cdot ${c}-${dividend}:${divisor}`,
    chainParts: [
      `${a}+${b}\\cdot ${c}-${dividend}:${divisor}`,
      `${a}+${product}-${quotient}`,
      `${a + product}-${quotient}`,
      `${result}`,
    ],
    result,
  });
}

function parenthesizedMixedTask() {
  const variant = randInt(0, 3);

  if (variant === 0) {
    const a = randInt(10, 28);
    const b = randInt(2, 12);
    const c = randInt(2, 6);
    const divisor = pick([2, 3, 4, 5]);
    const quotient = randInt(2, 9);
    const dividend = divisor * quotient;
    const sum = a + b;
    const product = sum * c;
    const result = product - quotient;
    return makeTask("OrderParenthesesMixedTask", {
      latex: `(${a}+${b})\\cdot ${c}-${dividend}:${divisor}`,
      chainParts: [
        `(${a}+${b})\\cdot ${c}-${dividend}:${divisor}`,
        `${sum}\\cdot ${c}-${quotient}`,
        `${product}-${quotient}`,
        `${result}`,
      ],
      result,
    });
  }

  if (variant === 1) {
    const a = randInt(8, 30);
    const b = randInt(2, 7);
    const c = randInt(2, 8);
    const d = randInt(2, 8);
    const divisor = pick([2, 3, 4, 5]);
    const quotient = randInt(2, 8);
    const dividend = divisor * quotient;
    const sum = c + d;
    const product = b * sum;
    const result = a + product - quotient;
    return makeTask("OrderParenthesesMixedTask", {
      latex: `${a}+${b}\\cdot (${c}+${d})-${dividend}:${divisor}`,
      chainParts: [
        `${a}+${b}\\cdot (${c}+${d})-${dividend}:${divisor}`,
        `${a}+${b}\\cdot ${sum}-${quotient}`,
        `${a}+${product}-${quotient}`,
        `${a + product}-${quotient}`,
        `${result}`,
      ],
      result,
    });
  }

  if (variant === 2) {
    const b = randInt(4, 9);
    const c = randInt(4, 9);
    const product = b * c;
    const divisor = pick([2, 3, 4, 5]);
    const maxQuotient = Math.max(2, Math.floor((product - 2) / divisor));
    const innerQuotient = randInt(2, maxQuotient);
    const dividend = product - divisor * innerQuotient;
    const a = randInt(8, 30);
    const result = a + innerQuotient;
    return makeTask("OrderParenthesesMixedTask", {
      latex: `${a}+(${b}\\cdot ${c}-${dividend}):${divisor}`,
      chainParts: [
        `${a}+(${b}\\cdot ${c}-${dividend}):${divisor}`,
        `${a}+(${product}-${dividend}):${divisor}`,
        `${a}+${product - dividend}:${divisor}`,
        `${a}+${innerQuotient}`,
        `${result}`,
      ],
      result,
    });
  }

  for (let i = 0; i < 200; i += 1) {
    const divisor = pick([2, 3, 4, 5]);
    const quotient = randInt(3, 9);
    const b = randInt(2, 7);
    const c = randInt(2, 8);
    const product = b * c;
    const a = randInt(12, 35);
    const dividend = a + product - divisor * quotient;
    if (dividend > 1) {
      return makeTask("OrderParenthesesMixedTask", {
        latex: `(${a}+${b}\\cdot ${c}-${dividend}):${divisor}`,
        chainParts: [
          `(${a}+${b}\\cdot ${c}-${dividend}):${divisor}`,
          `(${a}+${product}-${dividend}):${divisor}`,
          `${divisor * quotient}:${divisor}`,
          `${quotient}`,
        ],
        result: quotient,
      });
    }
  }

  return parenthesizedMixedTask();
}

export function generateTasks() {
  return [
    addSubTask(),
    subAddTask(),
    mulDivTask(),
    addMulTask(),
    parenthesesTask(),
    mixedTask(),
    parenthesizedMixedTask(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={data.latex} />.
    </p>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. 24"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const value = userAnswer?.parsed;
  return Number.isFinite(value) && value === data.result;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.result}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

function explanation(task) {
  const { type, data } = task;

  if (type === "OrderAddSubTask") {
    return (
      <>
        <p>Dodawanie i odejmowanie mają ten sam priorytet, więc liczymy od lewej do prawej.</p>
        <EquationChain parts={data.chainParts} />
      </>
    );
  }

  if (type === "OrderSubAddTask") {
    return (
      <>
        <p>Dodawanie i odejmowanie liczymy kolejno od lewej do prawej.</p>
        <EquationChain parts={data.chainParts} />
      </>
    );
  }

  if (type === "OrderMulDivTask") {
    return (
      <>
        <p>Mnożenie i dzielenie mają ten sam priorytet. W takim zapisie liczymy od lewej do prawej.</p>
        <EquationChain parts={data.chainParts} />
      </>
    );
  }

  if (type === "OrderAddMulTask") {
    return (
      <>
        <p>Mnożenie wykonujemy przed dodawaniem.</p>
        <EquationChain parts={data.chainParts} />
      </>
    );
  }

  if (type === "OrderParenthesesTask") {
    return (
      <>
        <p>Nawias liczymy jako pierwszy.</p>
        <EquationChain parts={data.chainParts} />
      </>
    );
  }

  if (type === "OrderParenthesesMixedTask") {
    return (
      <>
        <p>Nawias zmienia kolejność działań, więc zaczynamy od działania w nawiasie.</p>
        <EquationChain parts={data.chainParts} />
      </>
    );
  }

  return (
    <>
      <p>Najpierw wykonujemy mnożenie i dzielenie, a dopiero potem dodawanie i odejmowanie.</p>
      <EquationChain parts={data.chainParts} />
    </>
  );
}

export function renderExplanation(task) {
  return (
    <div style={{ textAlign: "left" }}>
      {explanation(task)}
      <p>
        <strong>Wynik:</strong> <InlineMath math={`${task.data.result}`} />.
      </p>
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  OrderAddSubTask: taskAdapter,
  OrderSubAddTask: taskAdapter,
  OrderMulDivTask: taskAdapter,
  OrderAddMulTask: taskAdapter,
  OrderParenthesesTask: taskAdapter,
  OrderMixedTask: taskAdapter,
  OrderParenthesesMixedTask: taskAdapter,
};

const orderOfOperationsSubtopic = {
  registry,
  generateTasks,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default orderOfOperationsSubtopic;
